import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { storefrontService } from '../services/storefront';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingCart, Eye } from 'lucide-react';
import { useShop } from '../context/ShopContext';

const CategoryPage = () => {
  const { slug } = useParams();
  const { addToCart, openQuickView } = useShop();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collectionTitle, setCollectionTitle] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        if (slug && slug !== 'alle') {
          const data = await storefrontService.getProductsByCollection(slug);
          setProducts(data);
          // Use the slug to derive a display name if no collection title
          setCollectionTitle(slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '));
        } else {
          const data = await storefrontService.getProducts();
          setProducts(data);
          setCollectionTitle('Alle produkter');
        }
      } catch (err) {
        console.error('Feil ved henting av produkter:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [slug]);

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <div className="mb-10">
          <div className="text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Hjem</Link> / <span className="text-foreground">{collectionTitle}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary">{collectionTitle}</h1>
          {!loading && (
            <p className="text-muted-foreground mt-2 text-sm">{products.length} produkter</p>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex flex-col space-y-3">
                <Skeleton className="h-[200px] sm:h-[300px] w-full rounded-xl" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))
          ) : products.length === 0 ? (
            <div className="col-span-full text-center py-20 text-muted-foreground">
              <p className="text-lg mb-4">Ingen produkter i denne kategorien ennå.</p>
              <Link to="/"><Button variant="outline">Tilbake til forsiden</Button></Link>
            </div>
          ) : (
            products.map((product) => (
              <div
                key={product.id}
                className="group bg-card rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border overflow-hidden flex flex-col h-full cursor-pointer"
                onClick={() => openQuickView(product)}
              >
                <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden bg-muted">
                  {product.badge && (
                    <Badge className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-accent text-white border-none px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs shadow-sm">
                      {product.badge}
                    </Badge>
                  )}
                  <div className="absolute top-3 right-3 z-10 flex-col gap-2 hidden sm:flex opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                    <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full shadow-sm hover:bg-white hover:text-primary" onClick={(e) => { e.stopPropagation(); openQuickView(product); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=400&auto=format&fit=crop'; }}
                  />
                  <div className="absolute bottom-4 left-4 right-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100 hidden sm:block">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white shadow-lg" onClick={(e) => { e.stopPropagation(); addToCart(product); }}>
                      <ShoppingCart className="mr-2 h-4 w-4" /> Legg i handlekurv
                    </Button>
                  </div>
                </div>

                <div className="p-3 sm:p-5 flex flex-col flex-grow">
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 font-medium tracking-wide">{product.category}</p>
                  <h3 className="font-semibold text-sm sm:text-lg leading-tight text-foreground mb-0.5 group-hover:text-primary transition-colors line-clamp-2">{product.name}</h3>
                  {product.scientific && <p className="text-[10px] sm:text-xs italic text-muted-foreground/80 mb-2 font-serif hidden sm:block">{product.scientific}</p>}
                  <div className="mt-auto">
                    {product.originalPrice && (
                      <span className="text-[10px] sm:text-xs text-muted-foreground line-through block">kr {product.originalPrice},-</span>
                    )}
                    <span className="text-sm sm:text-xl font-bold text-primary">kr {product.price},-</span>
                  </div>
                  <Button size="sm" className="mt-2 w-full bg-primary hover:bg-primary/90 text-white text-xs h-8 rounded-full sm:hidden" onClick={(e) => { e.stopPropagation(); addToCart(product); }}>
                    <ShoppingCart className="mr-1 h-3 w-3" /> Legg i kurv
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
